import { FundingPolicyType, isPolicyCountedInPlan } from '@shared';
import { AppState } from '@/model/store';
import { calculateTotalExistingAssetsValue } from './fundingSufficiency';

/**
 * Helper function to check if a policy was added via shop insurance.
 * Only returns true for active (non-deactivated) policies.
 */
export function isPolicyAddedViaShopInsurance(policy: any): boolean {
  return (
    (policy?.addedViaShopInsurance === true || policy?.addedViaShopInsurance === 'true') &&
    !policy?.isDeactivated
  );
}

// Lincoln MoneyGuard Fixed Advantage indemnity policies pay 80% of the
// contractual daily maximum. Shop-insurance quotes store the 100%
// (reimbursement) value; flipping payment method scales by this factor.
export const MONEY_GUARD_INDEMNITY_PAYOUT_RATE = 0.8;

export const MONEY_GUARD_DAILY_BENEFIT_FIELDS = [
  'dailyHomeBenefitAmount',
  'dailyIndependentLivingBenefitAmount',
  'dailyAssistedLivingBenefitAmount',
  'dailyNursingHomeBenefitAmount',
] as const satisfies ReadonlyArray<keyof PolicyFundingSource>;

export function isShopInsuranceMoneyGuard(policy: PolicyFundingSource): boolean {
  return (
    policy?.policyType === FundingPolicyType.moneyGuardFixedAdvantage &&
    isPolicyAddedViaShopInsurance(policy)
  );
}

export function scaleMoneyGuardBenefitFields(
  policy: PolicyFundingSource,
  toIndemnity: boolean,
): Partial<Pick<PolicyFundingSource, (typeof MONEY_GUARD_DAILY_BENEFIT_FIELDS)[number]>> {
  const scale = toIndemnity
    ? MONEY_GUARD_INDEMNITY_PAYOUT_RATE
    : 1 / MONEY_GUARD_INDEMNITY_PAYOUT_RATE;
  const updates: Partial<PolicyFundingSource> = {};
  for (const key of MONEY_GUARD_DAILY_BENEFIT_FIELDS) {
    const value = policy[key];
    if (typeof value === 'number') {
      updates[key] = value * scale;
    }
  }
  return updates;
}

// Some shop-insurance MoneyGuard policies are stored with
// dailyIndependentLivingBenefitAmount = 0 (e.g. legacy records written when
// reimbursement zeroed IL at write time). Indemnity MoneyGuard pays IL at the
// contractual daily max, so when flipping such a policy to indemnity we
// restore IL to match home daily before scaling — otherwise 0 * 0.8 stays 0
// and IL-phase monthly benefit calcs produce 0.
export function restoreIndependentLivingOnIndemnityFlip(
  policy: PolicyFundingSource,
  nextIsIndemnity: boolean,
): number | undefined {
  if (!nextIsIndemnity) return undefined;
  if (!isShopInsuranceMoneyGuard(policy)) return undefined;
  const currentIl = policy.dailyIndependentLivingBenefitAmount;
  if (typeof currentIl === 'number' && currentIl > 0) return undefined;
  const homeDaily = policy.dailyHomeBenefitAmount;
  if (typeof homeDaily !== 'number' || homeDaily <= 0) return undefined;
  return homeDaily;
}

/**
 * Calculates the total premium cost for all policies added via shop insurance
 * that count toward the plan.
 * Uses the pre-calculated policyTotalCost from each PolicyFundingSource.
 */
export function calculateTotalPoliciesCost(
  ltcPolicies: Record<string, PolicyFundingSource>,
): number {
  return Object.values(ltcPolicies).reduce((total, policy) => {
    if (!isPolicyAddedViaShopInsurance(policy) || !isPolicyCountedInPlan(policy)) return total;
    return total + (policy.policyTotalCost || 0);
  }, 0);
}

/**
 * Calculates the funding gap between total policy costs and allocated funds.
 * Returns positive number if there's a gap (costs > allocations),
 * zero if fully funded, or negative if over-funded.
 */
export function calculatePolicyFundingGap(fullState?: AppState): number {
  if (!fullState) return 0;

  const multipleFundingSources = fullState?.client?.data?.multipleFundingSources;
  if (!multipleFundingSources) return 0;

  const ltcPolicies = multipleFundingSources.ltcPolicy || {};
  const policiesAddedViaShop = Object.values(ltcPolicies).filter(policy =>
    isPolicyAddedViaShopInsurance(policy),
  );

  if (policiesAddedViaShop.length === 0) return 0;

  const totalPolicyCost = calculateTotalPoliciesCost(ltcPolicies);
  const totalExistingAssetsValue = calculateTotalExistingAssetsValue(multipleFundingSources);

  return totalPolicyCost - totalExistingAssetsValue;
}

/**
 * Gets all insurance quote IDs for policies added via shop insurance.
 * Returns a Set of quote IDs for efficient lookup.
 */
export function getShopInsuranceQuoteIds(
  multipleFundingSources?: MultipleFundingSources,
): Set<string> {
  const quoteIds = new Set<string>();
  const ltcPolicies = multipleFundingSources?.ltcPolicy || {};

  Object.values(ltcPolicies).forEach(policy => {
    if (isPolicyAddedViaShopInsurance(policy) && policy.insuranceQuoteId) {
      quoteIds.add(policy.insuranceQuoteId);
    }
  });

  return quoteIds;
}
